// @ts-strict-ignore
import produce from 'immer';
import unzip from 'lodash/unzip';
import uniq from 'lodash/uniq';
import moment from 'moment-timezone';

import {ALL_MATH_TYPES, PropertyType, ResourceType, RESULTS_OVERALL_SUM_KEY} from 'common/report/constants';
import {
  BehaviorShowClause,
  FilterClause,
  GroupClause,
  InsightsBookmarkSections,
  MetricMeasurement,
  ShowClause,
} from 'common/report/insights/util/types';
import {
  AnalysisType,
  ChartPlotStyle,
  ChartType,
  InsightsBookmarkParams,
  InsightsResourceType,
  MetricType,
  MultiAttributionType,
  ValueRepresentationType,
} from 'common/types/reports/bookmark';
import type {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {flattenSeriesToDepth} from 'common/report/insights/util';
import {SerializedCohort} from 'common/query-builder/cohorts-query-builder/models/types/serialized-cohort-types';
import {FiltersOperator, SelectorTypeDefinitions} from 'common/query-builder/query-entry-section/types';
import {AVATAR_PROPERTIES, NAME_PROPERTIES} from 'common/util/profiles';
import {fetchUsers} from 'common/report/queries/query-api/users-client';
import {Cohort} from 'common/query-builder/cohorts-query-builder/models/cohort';
import {User} from 'common/report/queries/query-api/types';
import {formatUserProperties} from 'common/util/format-property';
import {extractUserSummaryProperties} from 'common/report/util/profile/utils';
import {MPProperties, TIMESTAMP_FORMAT} from 'common/constants';
import {LocalizationStore} from 'common/i18n/store';
import {BlockedTimeWindow} from 'common/constants';
import {Behavior, CustomProperty} from 'common/types/reports/query';
import {CurrentProject} from 'common/report/model-resources/projects';
import {queryUtils, REPLAY_ID_PROPERTY} from 'common/report/queries/query-api/session-replay';

import {StringOperator} from '../property-filter-menu/models/types';
import {DateRangeType, TimeUnit} from '../date-range-picker/models/types';
import {DateRange} from '../date-range-picker/models/date-range';
import {RecordingEvents, RecordingMetadata, RecordingPageViewEvents} from './types';

export const REPLAY_RETENTION_DAYS = 30;
export const REPLAY_EVENT = `$mp_session_record`;
export const PAGE_VIEW_EVENT = `$mp_web_page_view`;
export const REPLAY_START_TIME_PROPERTY = `replay_start_time`;
export const REPLAY_ENV_PROPERTY = `replay_env`;

export const TRACKED_REPLAY_ID_PREFIX = `$tracked:`;
const TRACKED_REPLAY_ID_REGEXP = new RegExp(/^\$tracked:/);

const REPLAY_LENGTH_MS_PROPERTY = `replay_length_ms`;

export const RECORDING_METADATA_GROUPS: GroupClause[] = [
  {
    value: MPProperties.ReplayRetentionPeriod,
    resourceType: InsightsResourceType.Events,
    propertyType: PropertyType.Number,
  },
  {
    value: MPProperties.DistinctId,
    resourceType: InsightsResourceType.Events,
    propertyType: PropertyType.String,
  },
  {
    value: REPLAY_ID_PROPERTY,
    resourceType: InsightsResourceType.Events,
    search: ``,
    dataGroupId: null,
    propertyType: PropertyType.String,
    typeCast: null,
    unit: null,
  },
  {
    value: REPLAY_START_TIME_PROPERTY,
    resourceType: InsightsResourceType.Events,
    search: ``,
    dataGroupId: null,
    propertyType: PropertyType.String,
    typeCast: null,
    unit: null,
  },
  {
    value: REPLAY_ENV_PROPERTY,
    resourceType: InsightsResourceType.Events,
    search: ``,
    dataGroupId: null,
    propertyType: PropertyType.String,
    typeCast: null,
    unit: null,
  },
  {
    value: `$browser`,
    resourceType: InsightsResourceType.Events,
    search: ``,
    dataGroupId: null,
    propertyType: PropertyType.String,
    typeCast: null,
    unit: null,
  },
  {
    value: `$os`,
    resourceType: InsightsResourceType.Events,
    search: ``,
    dataGroupId: null,
    propertyType: PropertyType.String,
    typeCast: null,
    unit: null,
  },
  {
    value: `mp_lib`,
    resourceType: InsightsResourceType.Events,
    search: ``,
    dataGroupId: null,
    propertyType: PropertyType.String,
    typeCast: null,
    unit: null,
  },
  {
    value: `replay_start_url`,
    resourceType: InsightsResourceType.Events,
    search: ``,
    dataGroupId: null,
    propertyType: PropertyType.String,
    typeCast: null,
    unit: null,
  },
];

const COMMON_REPLAY_EVENT_GROUPS: GroupClause[] = [
  {
    value: `$time`,
    resourceType: InsightsResourceType.Events,
    search: ``,
    dataGroupId: null,
    propertyType: PropertyType.Datetime,
    typeCast: PropertyType.Number,
    unit: null,
  },
  {
    value: `$event_name`,
    resourceType: InsightsResourceType.Events,
    search: ``,
    dataGroupId: null,
    propertyType: PropertyType.String,
    typeCast: null,
    unit: null,
  },
];

const PAGE_VIEW_EVENT_GROUPS: GroupClause[] = [
  {
    value: REPLAY_ID_PROPERTY,
    resourceType: InsightsResourceType.Events,
    search: ``,
    dataGroupId: null,
    propertyType: PropertyType.String,
    typeCast: null,
    unit: null,
  },
  {
    value: `$current_url`,
    resourceType: InsightsResourceType.Events,
    search: ``,
    dataGroupId: null,
    propertyType: PropertyType.String,
    typeCast: null,
    unit: null,
  },
];

export const MAX_BY_TIME_MEASUREMENT: MetricMeasurement = {
  math: ALL_MATH_TYPES.MAX,
  perUserAggregation: null,
  rolling: null,
  cumulative: false,
  property: {
    dataset: `$mixpanel`,
    defaultType: PropertyType.Number,
    name: `$time`,
    resourceType: InsightsResourceType.Events,
    type: PropertyType.Number,
    unit: null,
  },
};

function getReplayIdCustomProp(fromDate?: string): CustomProperty {
  let numLookbackDays = REPLAY_RETENTION_DAYS;
  // optimize the query by minimizing the borrowed prop lookback window
  if (fromDate) {
    const daysSince = new DateRange({from: fromDate, type: DateRangeType.Since}).getIntervalDays() + 1;
    numLookbackDays = Math.min(numLookbackDays, daysSince);
  }

  const replayIdBehavior: Behavior = {
    aggregationOperator: ALL_MATH_TYPES.SESSION_REPLAY_ID_VALUE,
    event: {
      value: REPLAY_EVENT,
      label: REPLAY_EVENT,
    },
    filters: [],
    filtersOperator: FiltersOperator.And,
    property: {
      label: ``,
      type: PropertyType.String,
      propertyDefaultType: PropertyType.String,
      value: REPLAY_ID_PROPERTY,
      resourceType: ResourceType.Event,
    },
    multiAttribution: {type: MultiAttributionType.LastTouch},
    dateRange: {
      type: DateRangeType.RelativeAfter,
      window: {unit: TimeUnit.Day, value: numLookbackDays},
    },
  };

  return {
    name: `replay_id_custom_prop`,
    description: ``,
    // "$obs:" prefix to signify the Replay ID actually exists and wasn't borrowed
    displayFormula: `IFS(DEFINED( _A ), "${TRACKED_REPLAY_ID_PREFIX}" + _A, TRUE, _B)`,
    composedProperties: {
      _A: {
        type: `string`,
        label: REPLAY_ID_PROPERTY,
        value: REPLAY_ID_PROPERTY,
        resourceType: ResourceType.Event,
        propertyDefaultType: `string`,
      },
      _B: {
        behavior: replayIdBehavior,
      },
    },
  };
}
/**
 * - segment by replay ID (borrow from the session record event if not present) and distinct ID
 * - filter out undefined replay IDs
 */
function getSectionsForReportEntrypoint(
  project: CurrentProject,
  queryDateRange?: DateRange,
): Pick<InsightsBookmarkSections, `group` | `filter`> {
  const from = queryDateRange ? queryDateRange.getCalendarDatesAsOf(project.projectAsOfDate).from : null;
  const replayIdCustomProp = getReplayIdCustomProp(from);

  return {
    group: [
      {
        value: MPProperties.DistinctId,
        resourceType: InsightsResourceType.Events,
        propertyType: PropertyType.String,
      },
      {
        customProperty: replayIdCustomProp,
        resourceType: InsightsResourceType.Events,
        value: REPLAY_ID_PROPERTY,
        propertyType: PropertyType.String,
      },
    ],
    filter: [
      {
        dataset: `$mixpanel`,
        customProperty: replayIdCustomProp,
        filterType: PropertyType.String,
        defaultType: PropertyType.String,
        filterOperator: StringOperator.Set,
        filterValue: null,
        propertyObjectKey: null,
        value: REPLAY_ID_PROPERTY,
        dataGroupId: null,
        search: ``,
        resourceType: InsightsResourceType.Events,
        profileType: null,
      },
    ],
  };
}

export function isTrackedReplayId(replayId: string): boolean {
  return replayId.match(TRACKED_REPLAY_ID_REGEXP) !== null;
}

export const LATEST_REPLAYS_SHOW_CLAUSE: ShowClause = {
  behavior: {
    type: MetricType.Event,
    resourceType: InsightsResourceType.Events,
    dataGroupId: null,
    filters: [],
    name: REPLAY_EVENT,
    profileType: null,
  },
  measurement: MAX_BY_TIME_MEASUREMENT,
};

export const REPLAYS_LENGTH_SHOW_CLAUSE: ShowClause = {
  behavior: {
    type: MetricType.Event,
    resourceType: InsightsResourceType.Events,
    dataGroupId: null,
    filters: [],
    name: REPLAY_EVENT,
    profileType: null,
  },
  measurement: {
    math: ALL_MATH_TYPES.MAX,
    perUserAggregation: null,
    rolling: null,
    cumulative: false,
    property: {
      dataset: `$mixpanel`,
      defaultType: PropertyType.Number,
      name: REPLAY_LENGTH_MS_PROPERTY,
      resourceType: InsightsResourceType.Events,
      type: PropertyType.Number,
      unit: null,
    },
  },
};

/**
 * Base query to fetch metadata from $mp_session_record events, to be used as a starting point for further filtering.
 * We determine which event to pick by doing a max on the replay event duration to indicate how long the replay lasted and segment by replay start time to get when the replay started.
 */
const BASE_RECORDING_METADATA_QUERY: InsightsBookmarkParams = {
  sorting: {},
  columnWidths: {bar: {}},
  displayOptions: {
    analysis: AnalysisType.Linear,
    chartType: ChartType.Table,
    plotStyle: ChartPlotStyle.Standard,
    statSigControl: [],
    value: ValueRepresentationType.Absolute,
  },
  sections: {
    cohorts: [],
    filter: [],
    formula: [],
    group: RECORDING_METADATA_GROUPS,
    show: [REPLAYS_LENGTH_SHOW_CLAUSE],
    time: [
      {
        unit: TimeUnit.Day,
        value: 30,
      },
    ],
  },
};

const BASE_REPLAY_EVENTS_QUERY: InsightsBookmarkParams = {
  sorting: {},
  columnWidths: {bar: {}},
  displayOptions: {
    analysis: AnalysisType.Linear,
    chartType: ChartType.Table,
    plotStyle: ChartPlotStyle.Standard,
    statSigControl: [],
    value: ValueRepresentationType.Absolute,
  },
  sections: {
    cohorts: [],
    filter: [],
    formula: [],
    group: [],
    show: [
      {
        behavior: {
          type: MetricType.Event,
          resourceType: InsightsResourceType.Events,
          dataGroupId: null,
          filters: [],
          name: `$all_events`,
          profileType: null,
        },
        measurement: {
          math: ALL_MATH_TYPES.TOTAL,
          cumulative: false,
          perUserAggregation: null,
          property: null,
          rolling: null,
        },
      },
    ],
    time: [
      {
        unit: TimeUnit.Day,
        value: 30,
      },
    ],
  },
};

const BASE_PAGE_VIEW_EVENT_QUERY: InsightsBookmarkParams = {
  sorting: {},
  columnWidths: {bar: {}},
  displayOptions: {
    analysis: AnalysisType.Linear,
    chartType: ChartType.Table,
    plotStyle: ChartPlotStyle.Standard,
    statSigControl: [],
    value: ValueRepresentationType.Absolute,
  },
  sections: {
    cohorts: [],
    filter: [],
    formula: [],
    group: PAGE_VIEW_EVENT_GROUPS,
    show: [
      {
        behavior: {
          type: MetricType.Event,
          resourceType: InsightsResourceType.Events,
          dataGroupId: null,
          filters: [],
          name: PAGE_VIEW_EVENT,
          profileType: null,
        },
        measurement: MAX_BY_TIME_MEASUREMENT,
      },
    ],
    time: [
      {
        unit: TimeUnit.Day,
        value: 30,
      },
    ],
  },
};

export enum ReplaysDisabledReason {
  NONE = `none`,
  COHORT_DISABLED = `cohort_disabled`,
  RETENTION_EXCEEDED = `retention_exceeded`,
  NOT_INTEGRATED = `not_integrated`,
  FUNNEL_WITH_ATTRIBUTION = `funnel_with_attribution`,
}

function formatEventTime(replay: RecordingMetadata, timeSegmentValue: string) {
  const parsedTime = Number(timeSegmentValue.replaceAll(`,`, ``));
  return Math.max(parsedTime, replay.time);
}

function getReportReplaysDisabledReason({
  replayTimestampMs,
  currentProject,
  showClauses,
}: {
  replayTimestampMs: number;
  currentProject: CurrentProject;
  showClauses: BehaviorShowClause[];
}): ReplaysDisabledReason {
  if (!currentProject?.hasIntegratedSessionReplay) {
    return ReplaysDisabledReason.NOT_INTEGRATED;
  }

  const hasCohortClause = showClauses.some((clause) => clause.behavior.resourceType === InsightsResourceType.Cohorts);
  if (hasCohortClause) {
    return ReplaysDisabledReason.COHORT_DISABLED;
  }

  // Replay queries need to segment funnels by the step that was clicked on. If the funnel already has segmentation, we can use an
  // inline filter on the actionStep if it's provided. This won't work for first/last touch attribution though,
  // because we don't know which step the property value is coming from.
  const isFunnelWithAttribution = showClauses.some((clause) => {
    return (
      clause.behavior.type === MetricType.Funnel &&
      clause.measurement.actionMode &&
      clause.measurement.actionMode !== `step`
    );
  });
  if (isFunnelWithAttribution) {
    return ReplaysDisabledReason.FUNNEL_WITH_ATTRIBUTION;
  }

  const retentionMs = REPLAY_RETENTION_DAYS * 24 * 60 * 60 * 1000;
  if (new Date().getTime() - replayTimestampMs > retentionMs) {
    return ReplaysDisabledReason.RETENTION_EXCEEDED;
  }

  return ReplaysDisabledReason.NONE;
}

async function queryUsers(distinctIds: string[]): Promise<User[]> {
  const rawCohort: SerializedCohort = {
    groups: [
      {
        type: SelectorTypeDefinitions.CohortGroup,
        event: {
          resourceType: ResourceType.Cohort,
          value: `$all_users`,
          label: `All Users`,
        },
        filters: [
          {
            resourceType: ResourceType.User,
            propertyName: `$distinct_id`,
            propertyObjectKey: null,
            propertyDefaultType: PropertyType.String,
            propertyType: PropertyType.String,
            filterOperator: StringOperator.Equals,
            filterValue: distinctIds,
          } as PropertyFilter,
        ],
        filtersOperator: FiltersOperator.And,
        behavioralFiltersOperator: FiltersOperator.And,
        groupingOperator: null,
        property: null,
      },
    ],
  };

  const results = await fetchUsers({
    cohort: Cohort.parseCohort(rawCohort),
    includeAllUsers: false,
    columns: [MPProperties.LastSeen, ...AVATAR_PROPERTIES, ...NAME_PROPERTIES].map((value) => ({
      value,
      resourceType: ResourceType.User,
    })),
  });

  return results;
}

async function fetchEventsForReplay(
  replay: RecordingMetadata,
  currentProject?: CurrentProject,
): Promise<RecordingEvents> {
  // for single replays we don't need to use the extra borrowed property and can just narrow the date range to span the replay's length
  const startTimeUTCSeconds = replay.time - 1;
  const endTimeUTCSeconds = startTimeUTCSeconds + replay.duration / 1000 + 1;

  const fromDate = moment.tz(new Date(startTimeUTCSeconds * 1000), currentProject?.timezone).format(TIMESTAMP_FORMAT);
  const toDate = moment.tz(new Date(endTimeUTCSeconds * 1000), currentProject?.timezone).format(TIMESTAMP_FORMAT);

  const allEventsQuery = produce(BASE_REPLAY_EVENTS_QUERY, (draft) => {
    draft.sections.time = [
      {
        dateRangeType: DateRangeType.Between,
        value: [fromDate, toDate],
        unit: TimeUnit.Minute, // doesn't matter, but required
      },
    ];
    draft.sections.filter = [queryUtils.getDistinctIdFilter([replay.distinctId])];
    draft.sections.group = [
      ...COMMON_REPLAY_EVENT_GROUPS,
      {
        value: REPLAY_ID_PROPERTY,
        resourceType: InsightsResourceType.Events,
        search: ``,
        dataGroupId: null,
        propertyType: PropertyType.String,
        typeCast: null,
        unit: null,
      },
    ];
  });

  // Ignore sampling for this query so that we can get all relevant events. Some customers with ingestion time sampling have
  // their $all_events queries sampled, which can cause inconsistent behavior when listing events.
  const eventResults = await queryUtils.makeSessionReplayQuery({
    params: allEventsQuery,
    blockedReplayFrames: [],
    limit: 5000,
    ignoreSampling: true,
  });

  return {
    replayId: replay.replayId,
    replayEvents: queryUtils
      .flattenSeriesWithoutMetadata(eventResults, allEventsQuery.sections.group)
      .map(([path, metric], idx) => {
        const labeledSegmentValues = queryUtils.getLabeledSegmentValues(path, allEventsQuery.sections.group);
        const quantity = Object.values(metric)[0];
        return {
          replayId: replay.replayId,
          eventName: labeledSegmentValues[`$event_name`],
          eventTime: formatEventTime(replay, labeledSegmentValues[`$time`]),
          hasMatchingReplayIdProperty: labeledSegmentValues[REPLAY_ID_PROPERTY] === replay.replayId,
          key: `specificEventsQuery-${replay.replayId}-${idx}`,
          quantity,
        };
      }),
  };
}

async function fetchEventsForReplays(
  replays: RecordingMetadata[],
  currentProject?: CurrentProject,
): Promise<RecordingEvents[]> {
  const replayIds = replays.map((replay) => replay.replayId);
  const distinctIds = replays.map((replay) => replay.distinctId);

  // TODO: we can minorly optimize this query by making the lookback the earliest replay start time
  const minStartTimeUTCSeconds = Math.min(...replays.map((replay) => replay.time));
  const fromDate = moment
    .tz(new Date(minStartTimeUTCSeconds * 1000), currentProject?.timezone)
    .format(TIMESTAMP_FORMAT);
  const replayIdCustomProp = getReplayIdCustomProp(fromDate);
  const replayIdGroup: GroupClause = {
    customProperty: replayIdCustomProp,
    resourceType: InsightsResourceType.Events,
    value: REPLAY_ID_PROPERTY,
    propertyType: PropertyType.String,
  };

  const filterValue = replayIds.map((replayId) => `${TRACKED_REPLAY_ID_PREFIX}${replayId}`).concat(replayIds);
  const replayIdFilter: FilterClause = {
    customProperty: replayIdCustomProp,
    dataGroupId: null,
    dataset: `$mixpanel`,
    defaultType: PropertyType.String,
    filterOperator: StringOperator.Equals,
    filterType: PropertyType.String,
    filterValue,
    profileType: null,
    propertyObjectKey: null,
    resourceType: InsightsResourceType.Events,
    search: ``,
    value: REPLAY_ID_PROPERTY,
  };

  const allEventsQuery = produce(BASE_REPLAY_EVENTS_QUERY, (draft) => {
    draft.sections.group = [replayIdGroup, ...COMMON_REPLAY_EVENT_GROUPS];

    draft.sections.filter = [replayIdFilter, queryUtils.getDistinctIdFilter(distinctIds)];
  });

  // Ignore sampling for this query so that we can get all relevant events. Some customers with ingestion time sampling have
  // their $all_events queries sampled, which can cause inconsistent behavior when listing events.
  const eventResults = await queryUtils.makeSessionReplayQuery({
    params: allEventsQuery,
    blockedReplayFrames: [],
    limit: 5000,
    ignoreSampling: true,
  });

  const flatSeries = queryUtils.flattenSeriesWithoutMetadata(eventResults, allEventsQuery.sections.group);
  const res = flatSeries.map(([path, metric]) => {
    const labeledSegmentValues = queryUtils.getLabeledSegmentValues(path, allEventsQuery.sections.group);
    const quantity = Object.values(metric)[0];

    return {
      replayId: labeledSegmentValues[REPLAY_ID_PROPERTY].replace(TRACKED_REPLAY_ID_REGEXP, ``),
      eventName: labeledSegmentValues[`$event_name`],
      eventTime: labeledSegmentValues[`$time`],
      hasMatchingReplayIdProperty: isTrackedReplayId(labeledSegmentValues[REPLAY_ID_PROPERTY]),
      quantity,
    };
  });

  return replays.map((replay) => ({
    replayId: replay.replayId,
    replayEvents: res
      .filter((eventGroup) => eventGroup.replayId === replay.replayId)
      .map((eventGroup, idx) => ({
        eventName: eventGroup.eventName,
        eventTime: formatEventTime(replay, eventGroup.eventTime),
        quantity: eventGroup.quantity,
        hasMatchingReplayIdProperty: eventGroup.hasMatchingReplayIdProperty,
        key: `generalEventsQuery-${eventGroup.replayId}-${idx}`,
      })),
  }));
}

async function fetchPageViewsForReplays(replays: RecordingMetadata[]): Promise<RecordingPageViewEvents[]> {
  const replayIds = replays.map((replay) => replay.replayId);
  const distinctIds = replays.map((replay) => replay.distinctId);

  const pageViewEventsQuery = produce(BASE_PAGE_VIEW_EVENT_QUERY, (draft) => {
    draft.sections.filter = [
      ...draft.sections.filter,
      queryUtils.getDistinctIdFilter(distinctIds),
      {
        dataset: `$mixpanel`,
        value: REPLAY_ID_PROPERTY,
        resourceType: InsightsResourceType.Events,
        profileType: null,
        search: ``,
        dataGroupId: null,
        filterType: PropertyType.String,
        defaultType: PropertyType.String,
        filterOperator: StringOperator.Equals,
        filterValue: replayIds,
        propertyObjectKey: null,
      },
    ];
  });

  const eventResults = await queryUtils.makeSessionReplayQuery({
    params: pageViewEventsQuery,
    blockedReplayFrames: [],
    limit: 5000,
  });

  const flatSeries = queryUtils.flattenSeriesWithoutMetadata(eventResults, pageViewEventsQuery.sections.group);
  const res = flatSeries.map(([path, metric]) => {
    const labeledSegmentValues = queryUtils.getLabeledSegmentValues(path, pageViewEventsQuery.sections.group);
    const eventTime = Object.values(metric)[0];

    return {
      replayId: labeledSegmentValues[REPLAY_ID_PROPERTY],
      currentURL: labeledSegmentValues[`$current_url`],
      eventTime,
    };
  });

  return replays.map((replay) => ({
    replayId: replay.replayId,
    replayPageViewEvents: res
      .filter((eventGroup) => eventGroup.replayId === replay.replayId)
      .map((eventGroup) => ({
        eventTime: eventGroup.eventTime,
        currentURL: eventGroup.currentURL,
      })),
  }));
}

async function fetchRecordingsByIds({
  replayIds,
  distinctIds,
  blockedReplayFrames,
  i18n,
  checkForStitchedReplays,
}: {
  replayIds: string[];
  distinctIds: string[];
  blockedReplayFrames: BlockedTimeWindow[];
  i18n: LocalizationStore;
  checkForStitchedReplays?: boolean;
}): Promise<RecordingMetadata[]> {
  if (!replayIds?.length || !distinctIds?.length) {
    return [];
  }

  const trackedReplayIds = new Set<string>();
  for (const replayId of replayIds) {
    if (isTrackedReplayId(replayId)) {
      trackedReplayIds.add(replayId.replace(TRACKED_REPLAY_ID_REGEXP, ``));
    }
  }

  const params = produce(BASE_RECORDING_METADATA_QUERY, (draft) => {
    draft.sections.filter = [
      ...draft.sections.filter,
      queryUtils.getDistinctIdFilter(distinctIds),
      {
        dataset: `$mixpanel`,
        value: REPLAY_ID_PROPERTY,
        resourceType: InsightsResourceType.Events,
        profileType: null,
        search: ``,
        dataGroupId: null,
        filterType: PropertyType.String,
        defaultType: PropertyType.String,
        filterOperator: StringOperator.Equals,
        filterValue: replayIds.map((replayId) => replayId.replace(TRACKED_REPLAY_ID_REGEXP, ``)),
        propertyObjectKey: null,
      },
    ];
  });

  const [replayResults, userResults] = await Promise.all([
    queryUtils.makeSessionReplayQuery({params, blockedReplayFrames}),
    queryUsers(distinctIds),
  ]);

  const userSummaryByDistinctId = userResults.reduce((acc, user) => {
    const formattedProperties = formatUserProperties(user.$properties, {i18n});
    acc.set(user.$distinct_id, extractUserSummaryProperties(formattedProperties));
    return acc;
  }, new Map());

  // fill in any users without profiles with default values
  for (const distinctId of distinctIds) {
    if (!userSummaryByDistinctId.has(distinctId)) {
      userSummaryByDistinctId.set(distinctId, extractUserSummaryProperties(new Map()));
    }
  }
  const flatSeries = queryUtils.flattenSeriesWithoutMetadata(replayResults, params.sections.group);
  return processAndGetMostRecentReplays(
    flatSeries,
    checkForStitchedReplays,
    userSummaryByDistinctId,
    trackedReplayIds,
    params,
  );
}

export function processAndGetMostRecentReplays(
  flatSeries: Array<[string[], Record<string, number>]>,
  checkForStitchedReplays: boolean,
  userSummaryByDistinctId: Map<string, Record<string, string>>,
  trackedReplayIds: Set<string>,
  params: InsightsBookmarkParams,
): RecordingMetadata[] {
  const mostRecentReplaysMap = new Map();
  flatSeries.forEach(([path, metric]) => {
    const labeledSegmentValues = queryUtils.getLabeledSegmentValues(path, params.sections.group);
    const duration = Object.values(metric)[0];
    const distinctId = labeledSegmentValues[MPProperties.DistinctId];

    const userSummaryProperties = userSummaryByDistinctId.get(distinctId) ?? {};
    const startingURL = labeledSegmentValues[`replay_start_url`];
    const replayId = labeledSegmentValues[REPLAY_ID_PROPERTY];
    if (
      !mostRecentReplaysMap.has(replayId) ||
      (mostRecentReplaysMap.has(replayId) && mostRecentReplaysMap.get(replayId).duration < duration)
    ) {
      mostRecentReplaysMap.set(replayId, {
        time: Number(labeledSegmentValues[REPLAY_START_TIME_PROPERTY]),
        distinctId,
        email: userSummaryProperties.email,
        name: userSummaryProperties.name,
        replayId,
        avatarUrl: userSummaryProperties.avatarUrl,
        duration,
        replayEnv: labeledSegmentValues[REPLAY_ENV_PROPERTY],
        browser: labeledSegmentValues[`$browser`],
        os: labeledSegmentValues[`$os`],
        source: labeledSegmentValues[`mp_lib`],
        startingURL: startingURL === `undefined` ? null : startingURL,
        retentionPeriod: labeledSegmentValues[`$mp_replay_retention_period`]
          ? Number(labeledSegmentValues[`$mp_replay_retention_period`])
          : null,
        isStitchedFromEvent: checkForStitchedReplays
          ? !trackedReplayIds.has(labeledSegmentValues[REPLAY_ID_PROPERTY])
          : false,
      });
    }
  });
  return Array.from(mostRecentReplaysMap.values());
}

async function fetchRecordingsForDistinctId(
  distinctId: string,
  blockedReplayFrames: BlockedTimeWindow[],
  i18n: LocalizationStore,
): Promise<RecordingMetadata[]> {
  const params = produce(BASE_RECORDING_METADATA_QUERY, (draft) => {
    draft.sections.filter = [...draft.sections.filter, queryUtils.getDistinctIdFilter([distinctId])];
  });

  const [replayResults, userResults] = await Promise.all([
    queryUtils.makeSessionReplayQuery({params, blockedReplayFrames}),
    queryUsers([distinctId]),
  ]);

  // some strange transformations, but important to stay consistent with users page / profile app
  const formattedUserProps = formatUserProperties(userResults[0]?.$properties ?? {}, {i18n});
  const userSummaryProperties = extractUserSummaryProperties(formattedUserProps);

  return queryUtils.flattenSeriesWithoutMetadata(replayResults, params.sections.group).map(([path, metric]) => {
    const labeledSegmentValues = queryUtils.getLabeledSegmentValues(path, params.sections.group);
    const duration = Object.values(metric)[0];
    const startingURL = labeledSegmentValues[`replay_start_url`];

    return {
      time: Number(labeledSegmentValues[REPLAY_START_TIME_PROPERTY]),
      distinctId,
      email: userSummaryProperties?.email,
      name: userSummaryProperties?.name,
      replayId: labeledSegmentValues[REPLAY_ID_PROPERTY],
      avatarUrl: userSummaryProperties?.avatarUrl,
      duration,
      replayEnv: labeledSegmentValues[REPLAY_ENV_PROPERTY],
      browser: labeledSegmentValues[`$browser`],
      os: labeledSegmentValues[`$os`],
      source: labeledSegmentValues[`mp_lib`],
      retentionPeriod: labeledSegmentValues[`$mp_replay_retention_period`]
        ? Number(labeledSegmentValues[`$mp_replay_retention_period`])
        : null,
      startingURL: startingURL === `undefined` ? null : startingURL,
      isStitchedFromEvent: false,
    };
  });
}

async function fetchLatestRecordings(
  blockedReplayFrames: BlockedTimeWindow[],
  i18n: LocalizationStore,
): Promise<RecordingMetadata[]> {
  const params = produce(BASE_RECORDING_METADATA_QUERY, (draft) => {
    draft.sections.time = [
      {
        unit: TimeUnit.Day,
        value: 30,
      },
    ];
    draft.sections.show = [LATEST_REPLAYS_SHOW_CLAUSE];
  });

  // TODO: this can be optimized to only make 1 insights query
  return queryUtils.makeSessionReplayQuery({params, blockedReplayFrames}).then((results) => {
    const distinctIdsWithReplays: string[][] = flattenSeriesToDepth(results.series, params.sections.group.length)
      .map(([segments]) => {
        const segVals = queryUtils.getLabeledSegmentValues(segments, params.sections.group);
        return [segVals.$distinct_id, segVals.$mp_replay_id];
      })
      // filter out unneeded segments
      .filter(([segments]) => !segments.includes(RESULTS_OVERALL_SUM_KEY) && !segments.includes(`undefined`));

    const [distinctIds, replayIds] = unzip(distinctIdsWithReplays);
    return fetchRecordingsByIds({
      replayIds: uniq(replayIds),
      distinctIds: uniq(distinctIds),
      blockedReplayFrames,
      i18n,
    });
  });
}

// export for mockable utils
export const sessionRecordingQueryUtils = {
  getSectionsForReportEntrypoint,
  getReportReplaysDisabledReason,
  fetchEventsForReplay,
  fetchEventsForReplays,
  fetchPageViewsForReplays,
  fetchRecordingsByIds,
  fetchRecordingsForDistinctId,
  fetchLatestRecordings,
};
